import React, {useMemo, useEffect, useState, useRef} from 'react'
import {debounce} from 'lodash'
import { Input, FormGroup, Label, Row, Col } from 'reactstrap'
import { ReactComponent as LinkIcon } from '../../../assets/icons/linkable.svg'
import { useSelectedVariantContext } from '../context/SelectedVariantContext'
import LinkableOnUrl from '../../../assets/images/linkable-on.jpg'
import LinkableOffUrl from '../../../assets/images/linkable-off.jpg'
import createDebug from 'debug'
import {actionAddImageLayer, actionAddImageLayerToSide, changeLinkableSide, removeAllSideLayers} from '../context/SelectedVariantContext/action'
import {
    onCapture,
    mergeAttributesByDesign,
    replaceUnderline
} from '../context/SelectedVariantContext/helper'
import {getLocalData, setLocalData} from '../../../services/StorageServices'

const LinkDesignSide = () => {
    const {
        state: {selectedAttribute = {}, selectedSide, designs, pickerAttributes, loadingImageLayer, linkableSides = []},
        dispatch
    } = useSelectedVariantContext()

    const HIGHLIGHT_KEY = 'linkable_highlight_shown'
    const [toggle, setToggle] = React.useState(false);
    const debug = createDebug("pf126:PLAT2-472");
    const [objImageCrop, setObjImageCrop] = useState(null)
    const [itemLinkable, setItemLinkable] = useState({})
    const [isActive, setIsActive] = React.useState(false)
    const [showHighlight, setShowHighlight] = useState(false)
    const [imgLoaded, setImgLoaded] = useState({
        on: false,
        off: false
    })

    const handleSwitchChange = async (e) => {
        dispatch(removeAllSideLayers(selectedSide))
        dispatch(changeLinkableSide(selectedSide, e.target.checked));
        if (e.target.checked && objImageCrop) {
            dispatch(actionAddImageLayer({
                ...objImageCrop,
                isLock: true
            }))
        }
    };

    useEffect(() => {
        setToggle(false)
    }, [selectedSide])

    useEffect(() => {
        if (linkableSides.length > 0) {
            const itemLink = linkableSides.find(item => item.linkedAttrIds.includes(selectedAttribute._id))
            if (itemLink.linkableSides !== undefined) {
                setItemLinkable(itemLink.linkableSides || {})
                const {childs = []} = itemLink.linkableSides
                if (childs.length > 0) {
                    setIsActive(childs.find(item => item.side === selectedSide) !== undefined)
                }
            }
        }
    }, [linkableSides, selectedSide])

    useEffect(() => {
        const hasShown = getLocalData(HIGHLIGHT_KEY)

        if (!hasShown) {
            setShowHighlight(true)
            setLocalData(HIGHLIGHT_KEY, '1')
        }
    }, [])

    // Ref to prevent infinite loops and track pending captures
    const captureLockRef = useRef(false);

    // Ref to track last processed parent layers to avoid re-processing
    const lastProcessedLayersRef = useRef(null);

    // Worker ref to manage the lifecycle of the web worker
    const workerRef = useRef(null);

    // Initialize worker on mount
    useEffect(() => {
        workerRef.current = new Worker(new URL('../../../webworkers/capture-crop.worker.js', import.meta.url), { type: 'module' });

        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
            }
        };
    }, []);

    // Lấy layers của mặt parent (front) một cách an toàn
    // Serialize để chỉ trigger khi layers của mặt parent thực sự thay đổi
    const parentSideLayers = useMemo(() => {
        if (!itemLinkable?.parent) return null
        if (itemLinkable.childs.length > 0) {
            const childLinkabled = itemLinkable.childs.find(item => item.onLinkable);
            if (!childLinkabled) {
                // Nếu child side đang bật linkable, mới lấy layers parent để capture
                return null; // Trả về null để không trigger capture
            }
        }
        const {parent: parentSide} = itemLinkable
        const design = designs.find(d => d.linkedAttrIds.includes(selectedAttribute?._id))
        // CHỈ lấy layers của parent side, KHÔNG lấy child side để tránh vòng lặp
        const layers = design?.side?.[parentSide]?.layers || []
        const safeZone = selectedAttribute?.safeZone?.[parentSide] || {}
        
        // Return JSON object với flag empty để detect cả 2 trạng thái
        // Điều này giúp:
        // 1. Tránh capture lần đầu khi chưa có layers
        // 2. Detect được khi xóa hết layers (từ có → không có)
        if (layers.length === 0) {
            return JSON.stringify({ empty: true, background: safeZone.fill })
        }
        
        // Serialize để so sánh chính xác
        return JSON.stringify({
            empty: false,
            layers: layers.map(layer => ({
                id: layer.id,
                x: layer.x,
                y: layer.y,
                hasPattern: layer.hasPattern,
                width: layer.width,
                height: layer.height,
                scaleX: layer.scaleX,
                scaleY: layer.scaleY,
                fill: layer.fill,
                background: safeZone.fill,
                rotation: layer.rotation
            }))
        })
    }, [designs, itemLinkable, selectedAttribute?.safeZone[selectedSide]?.fill])

    const debounceLinkableSides = debounce(() => {
        // Nếu không có layers thì không làm gì
        if (!parentSideLayers) {
            captureLockRef.current = false;
            return;
        }

        // Nếu đang loading image, reset lastProcessedLayersRef để khi loading xong sẽ capture lại
        if (loadingImageLayer) {
            debug('Images still loading, resetting lastProcessedLayersRef for next capture');
            lastProcessedLayersRef.current = null;
            return;
        }

        // Kiểm tra xem parent layers có thực sự thay đổi không
        // NHƯNG nếu lastProcessedLayersRef là null (sau khi loading xong), vẫn cho chạy
        if (lastProcessedLayersRef.current === parentSideLayers && lastProcessedLayersRef.current !== null) {
            debug('Parent layers unchanged, skipping capture');
            return;
        }

        const actionCaptureParentSide = async () => {
            if (!itemLinkable?.parent) return

            const {parent: parentSide, childs} = itemLinkable

            const childInfo = childs[0]
            const child = childInfo.side
            const parentInfo = childInfo.parentInfo


            // Prevent infinite loops
            if (captureLockRef.current) {
                debug('Capture already in progress, skipping');
                return;
            }

            // Lưu lại parent layers đã xử lý
            lastProcessedLayersRef.current = parentSideLayers;

            // Lock to prevent re-entrant calls
            captureLockRef.current = true;

            const attrsData = mergeAttributesByDesign(pickerAttributes, designs)
            if (!attrsData.length || !attrsData[0].length) {
                captureLockRef.current = false;
                return;
            }

            const stageRef = selectedAttribute.ref[parentSide]
            const safeZone = selectedAttribute.safeZone[parentSide]
            const mockup = selectedAttribute.mockup
            const sizeParentSide = mockup.preview_v2[parentSide].size || {}

            if (!stageRef) {
                captureLockRef.current = false;
                return;
            }

            try {
                // Parse parentSideLayers để check xem có layers không
                const layersData = JSON.parse(parentSideLayers)

                // Nếu không có layers (empty), xóa layer ở child side
                if (layersData.empty && layersData.background === '#ffffff') {
                    const childLinkable = itemLinkable.childs.find(item => item.side === child);
                    if (childLinkable?.onLinkable && selectedSide !== child) {
                        dispatch(removeAllSideLayers(child));
                    }
                    captureLockRef.current = false;
                    return;
                }

                const capture = await onCapture({ ...safeZone, stageRef, side: parentSide, mockup }, {isPublish: true})
                const {artwork = {}} = capture
                if (artwork?.src) {
                    // Use Worker for cropping
                    if (workerRef.current) {
                        const worker = workerRef.current;

                        const imageName = `linked-design-${child}-${Date.now()}.png`;

                        // Send message to worker
                        worker.postMessage({
                            imageUrl: artwork.src,
                            originalWidth: sizeParentSide.width || 0,
                            originalHeight: sizeParentSide.height || 0,
                            cropLeft: parentInfo.position.left,
                            cropTop: parentInfo.position.top,
                            cropWidth: parentInfo.size.width,
                            cropHeight: parentInfo.size.height,
                            imageName: imageName,
                            outputFormat: 'image/png',
                            quality: 1
                        });

                        // Wait for result from worker
                        const handleWorkerMessage = (event) => {
                            const { success, data, error } = event.data;

                            if (success) {
                                const croppedImageObj = data;
                                setObjImageCrop(croppedImageObj);
                                debug(`${child} image object`, croppedImageObj);

                                // Kiểm tra xem child side có bật linkable và không phải là side hiện tại
                                const childLinkable = itemLinkable.childs.find(item => item.side === child);
                                if (childLinkable?.onLinkable) {
                                    // Dispatch với flag để tránh trigger lại capture
                                    dispatch(actionAddImageLayerToSide({
                                        imageObj: {
                                            ...croppedImageObj,
                                            isLock: true
                                        },
                                        side: child,
                                        skipCapture: true // Flag để tránh vòng lặp
                                    }));
                                }
                            } else {
                                console.error(`Error cropping image in worker: ${error}`);
                            }

                            // Clean up listener
                            worker.removeEventListener('message', handleWorkerMessage);
                            // Unlock after worker completes
                            captureLockRef.current = false;
                        };

                        worker.addEventListener('message', handleWorkerMessage);
                    } else {
                        captureLockRef.current = false;
                    }
                } else {
                    captureLockRef.current = false;
                }
            } catch (err) {
                console.error(`Error in actionCaptureParentSide: ${err.message}`);
                captureLockRef.current = false;
            }
        }
        actionCaptureParentSide()
    }, 200)

    useEffect(() => {
        if (loadingImageLayer) return

        if (selectedAttribute?.mockup?.meta?.use_client_mockup) {
            debounceLinkableSides()
        }

        return () => {
            debounceLinkableSides.cancel()
        }
    }, [parentSideLayers, loadingImageLayer])

    if ( itemLinkable.parent === undefined ) return null

    return (
        <div className={`LinkDesignSide ${isActive ? 'd-block' : 'd-none'}`} style={{zIndex: showHighlight ? 9999 : 11}}>
            {toggle && <div className='bgLinkDesignSide' onClick={() => setToggle(false)} />}
            <span
                onClick={() => setToggle(!toggle)}
                id="iconTogglePopup_linkable"
                className={`
                    IconLinkable
                    cursor-pointer
                    ${showHighlight ? 'highlight-linkable' : ''}
                    ${
                                itemLinkable.childs.find(item => item.side === selectedSide)?.onLinkable
                                    ? 'linked'
                                    : ''
                            }
                `}
                        >
                <LinkIcon width={24} height={24} />
            </span>
            {showHighlight && (
                <div
                    className="linkable-overlay"
                    onClick={() => setShowHighlight(false)}
                />
            )}
            {(toggle || showHighlight) && (
                <div className={`ModalLinkDesignSide ${showHighlight ? 'showHighlight' : ''}`}>
                    <FormGroup switch>
                        <Input
                            id="switchLinkable-side"
                            type="switch"
                            role="switch"
                            checked={itemLinkable.childs.find(item => item.side === selectedSide) !== undefined ? itemLinkable.childs.find(item => item.side === selectedSide).onLinkable : false}
                            onChange={handleSwitchChange}
                        />
                        <Label className="LinkableLabel">
                            Match <span className="text-capitalize">{replaceUnderline(selectedSide)}</span>-side design
                            with <span className="text-capitalize">{replaceUnderline(itemLinkable?.parent)}</span>-side
                        </Label>
                    </FormGroup>
                    <Row columns={2} className="ImagePreviewLinkable">
                        <Col className="text-center">
                            {!imgLoaded.on && (
                                <div className="image-loading">
                                    <span className="spinner-border spinner-border-sm text-primary" />
                                </div>
                            )}

                            <img
                                src={LinkableOnUrl}
                                alt=""
                                style={{display: imgLoaded.on ? 'block' : 'none'}}
                                onLoad={() =>
                                    setImgLoaded(prev => ({...prev, on: true}))
                                }
                            />
                            <div>Matching: <span className="text-primary font-weight-500">ON</span></div>
                        </Col>
                        <Col className='text-center'>
                            {!imgLoaded.off && (
                                <div className='image-loading'>
                                    <span className='spinner-border spinner-border-sm text-primary' />
                                </div>
                            )}

                            <img
                                src={LinkableOffUrl}
                                alt=''
                                style={{display: imgLoaded.off ? 'block' : 'none'}}
                                onLoad={() =>
                                    setImgLoaded(prev => ({...prev, off: true}))
                                }
                            />
                            <div>Matching: <span className='text-danger font-weight-500'>OFF</span></div>
                        </Col>
                    </Row>
                    <Label>
                        Using this feature is the only way to align your design to continue seamlessly
                        from the <span className="text-capitalize">{replaceUnderline(itemLinkable?.parent)}</span>-side
                        onto
                        the <span className="text-capitalize">{replaceUnderline(selectedSide)}</span>-side.
                    </Label>
                </div>
            )}
        </div>
    )
}

export default LinkDesignSide
